import React, { useState, useEffect, useMemo } from 'react';
import { useSelector } from 'react-redux';
import {
  Box,
  Card,
  CardContent,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  CircularProgress,
  Chip,
  Paper,
  Divider
} from '@mui/material';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer
} from 'recharts';
import configServ from '../services/config';

const AttendanceGraph = () => {
  const { company_id } = useSelector((state) => state.GlobalVariables);

  const [selectedFilter, setSelectedFilter] = useState('today');
  const [loading, setLoading] = useState(false);
  const [graphDataResponse, setGraphDataResponse] = useState(null);

  // Fetch Attendance Data according to selected timeframe
  const fetchAttendanceData = async () => {
    if (!company_id) return;

    setLoading(true);
    try {
      let response = null;

      if (selectedFilter === 'today') {
        response = await configServ.departmentAttendanceToday({ company_id });
      } else if (selectedFilter === 'current_month') {
        response = await configServ.departmentAttendanceCurrentMonth({ company_id });
      } else if (selectedFilter === 'last_month') {
        response = await configServ.departmentAttendanceLastMonth({ company_id });
      }

      if (response) {
        setGraphDataResponse(response);
      }
    } catch (error) {
      console.error("Error fetching attendance graph data:", error);
      setGraphDataResponse([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAttendanceData();
  }, [selectedFilter, company_id]);

  const handleFilterChange = (e) => {
    setSelectedFilter(e.target.value);
  };

  // Process Chart Data and Metrics based on API Response
  const processedChartData = useMemo(() => {
    if (!graphDataResponse || !graphDataResponse.data) {
      return { chartData: [], metrics: { totalStaff: 0, totalPresentPct: 0, totalAbsentPct: 0, percentage: 0 }, maxYValue: 100 };
    }

    const overall = graphDataResponse.overall || {};

    const chartData = graphDataResponse.data.map((dept) => {
      // API se percentage use karein, agar missing ho toh calculate karein
      const presentPct = dept.attendance_percentage !== undefined 
        ? Number(dept.attendance_percentage) 
        : (dept.total_possible ? Number(((dept.total_present / dept.total_possible) * 100).toFixed(2)) : 0);

      const absentPct = dept.absent_percentage !== undefined 
        ? Number(dept.absent_percentage) 
        : (dept.total_possible ? Number(((dept.total_absent / dept.total_possible) * 100).toFixed(2)) : 0);

      return {
        department_id: dept.department_id,
        department_name: dept.department_name,
        total_staff: dept.total_staff || 0,
        present: presentPct, // percentage value for chart bar
        absent: absentPct,   // percentage value for chart bar
        total_possible: dept.total_possible || 0,
        attendance_percentage: presentPct,
        absent_percentage: absentPct
      };
    });

    // Dynamic Y-Max up to 100%
    const maxVal = Math.max(
      ...chartData.map((d) => Math.max(d.present, d.absent)),
      10
    );
    const maxYValue = Math.min(100, Math.ceil(maxVal * 1.15) || 100);

    // Overall metrics calculation in %
    const overallPresentPct = overall.attendance_percentage !== undefined
      ? Number(overall.attendance_percentage)
      : (overall.total_possible ? Number(((overall.total_present / overall.total_possible) * 100).toFixed(2)) : 0);

    const overallAbsentPct = overall.absent_percentage !== undefined
      ? Number(overall.absent_percentage)
      : (overall.total_possible ? Number(((overall.total_absent / overall.total_possible) * 100).toFixed(2)) : 0);

    return {
      chartData,
      maxYValue,
      metrics: {
        totalStaff: overall.total_staff || 0,
        totalPresentPct: overallPresentPct,
        totalAbsentPct: overallAbsentPct,
        percentage: overallPresentPct
      }
    };
  }, [graphDataResponse]);

  // Dynamic width based on data items count for horizontal scrolling
  const minGraphWidth = useMemo(() => {
    const dataCount = processedChartData.chartData.length || 0;
    return Math.max(500, dataCount * 120);
  }, [processedChartData.chartData]);

  return (
    <Box sx={{ width: '100%', minHeight: '100vh', backgroundColor: '#f8fafc', p: { xs: 2, md: 3 } }}>
      <Paper elevation={0} sx={{ p: { xs: 2, md: 3 }, borderRadius: 3, background: '#ffffff', mb: 3, border: '1px solid #e2e8f0' }}>
        
        {/* Top Header & Timeframe Filter */}
        <Grid container spacing={2} alignItems="center" justifyContent="space-between">
          <Grid item xs={12} sm={6}>
            <Typography variant="h5" fontWeight="700" color="#0f172a">
              Attendance Analytics
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Department-wise summary graph with scroll view
            </Typography>
          </Grid>

          <Grid item xs={12} sm={6} sx={{ display: 'flex', justifyContent: { xs: 'flex-start', sm: 'flex-end' } }}>
            <FormControl size="small" sx={{ minWidth: 180 }}>
              <InputLabel id="filter-label">Timeframe</InputLabel>
              <Select
                labelId="filter-label"
                value={selectedFilter}
                label="Timeframe"
                onChange={handleFilterChange}
              >
                <MenuItem value="today">Today</MenuItem>
                <MenuItem value="current_month">Current Month</MenuItem>
                <MenuItem value="last_month">Last Month</MenuItem>
              </Select>
            </FormControl>
          </Grid>
        </Grid>

        <Divider sx={{ my: 3 }} />

        {/* Dynamic Metric Cards */}
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={6} sm={3}>
            <Card variant="outlined" sx={{ borderRadius: 2, background: '#f8fafc' }}>
              <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                <Typography variant="caption" color="text.secondary" fontWeight="600">TOTAL STAFF</Typography>
                <Typography variant="h5" fontWeight="700" color="#0f172a">
                  {processedChartData.metrics.totalStaff}
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={6} sm={3}>
            <Card variant="outlined" sx={{ borderRadius: 2, background: '#f0fdf4', borderColor: '#bbf7d0' }}>
              <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                <Typography variant="caption" color="#166534" fontWeight="600">PRESENT %</Typography>
                <Typography variant="h5" fontWeight="700" color="#15803d">
                  {processedChartData.metrics.totalPresentPct}%
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={6} sm={3}>
            <Card variant="outlined" sx={{ borderRadius: 2, background: '#fef2f2', borderColor: '#fecaca' }}>
              <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                <Typography variant="caption" color="#991b1b" fontWeight="600">ABSENT %</Typography>
                <Typography variant="h5" fontWeight="700" color="#b91c1c">
                  {processedChartData.metrics.totalAbsentPct}%
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={6} sm={3}>
            <Card variant="outlined" sx={{ borderRadius: 2, background: '#eff6ff', borderColor: '#bfdbfe' }}>
              <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                <Typography variant="caption" color="#1e40af" fontWeight="600">ATTENDANCE %</Typography>
                <Typography variant="h5" fontWeight="700" color="#1d4ed8">
                  {processedChartData.metrics.percentage}%
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* Section Title and Date Range */}
        <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1 }}>
          <Typography variant="subtitle1" fontWeight="600">
            Department Breakdown (%)
          </Typography>
          {graphDataResponse?.start_date && (
            <Chip 
              label={`${graphDataResponse.start_date} to ${graphDataResponse.end_date}`} 
              size="small" 
              color="primary"
              variant="outlined"
            />
          )}
        </Box>

        {/* Graph Area */}
        {loading ? (
          <Box sx={{ display: 'flex', height: 380, justifyContent: 'center', alignItems: 'center' }}>
            <CircularProgress size={40} />
          </Box>
        ) : processedChartData.chartData.length === 0 ? (
          <Box sx={{ display: 'flex', height: 380, justifyContent: 'center', alignItems: 'center' }}>
            <Typography color="text.secondary">No attendance data available.</Typography>
          </Box>
        ) : (
          <Box sx={{ display: 'flex', width: '100%', height: 400, position: 'relative' }}>
            
            {/* Fixed Y-Axis Container */}
            <Box sx={{ width: 60, height: '100%', flexShrink: 0, zIndex: 2, background: '#ffffff' }}>
              <ResponsiveContainer width="100%" height="100%">
                <BarChart
                  data={processedChartData.chartData}
                  margin={{ top: 20, right: 0, left: -10, bottom: 60 }}
                >
                  <YAxis 
                    domain={[0, processedChartData.maxYValue]} 
                    tickFormatter={(val) => `${val}%`}
                    tick={{ fill: '#475569', fontSize: 12 }} 
                    axisLine={false}
                    tickLine={false}
                  />
                </BarChart>
              </ResponsiveContainer>
            </Box>

            {/* Scrollable Graph Bars */}
            <Box 
              sx={{ 
                flex: 1, 
                overflowX: 'auto', 
                overflowY: 'hidden',
                pb: 1,
                '&::-webkit-scrollbar': { height: 8 },
                '&::-webkit-scrollbar-track': { backgroundColor: '#f1f5f9', borderRadius: 4 },
                '&::-webkit-scrollbar-thumb': { backgroundColor: '#cbd5e1', borderRadius: 4, '&:hover': { backgroundColor: '#94a3b8' } }
              }}
            >
              <Box sx={{ width: '100%', minWidth: `${minGraphWidth}px`, height: '100%' }}>
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart
                    data={processedChartData.chartData}
                    margin={{ top: 20, right: 30, left: 10, bottom: 25 }}
                    barGap={6}
                  >
                    <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f1f5f9" />
                    <YAxis domain={[0, processedChartData.maxYValue]} hide />
                    <XAxis 
                      dataKey="department_name" 
                      tick={{ fill: '#475569', fontSize: 12 }} 
                      interval={0}
                    />
                    <Tooltip 
                      formatter={(value, name) => [`${value}%`, name]}
                      contentStyle={{ 
                        backgroundColor: '#ffffff', 
                        borderRadius: '8px', 
                        border: '1px solid #e2e8f0', 
                        boxShadow: '0 4px 6px -1px rgba(0,0,0,0.1)' 
                      }}
                    />
                    <Legend wrapperStyle={{ paddingTop: '10px' }} />
                    <Bar dataKey="present" name="Present (%)" fill="#22c55e" radius={[4, 4, 0, 0]} maxBarSize={45} />
                    <Bar dataKey="absent" name="Absent (%)" fill="#ef4444" radius={[4, 4, 0, 0]} maxBarSize={45} />
                  </BarChart>
                </ResponsiveContainer>
              </Box>
            </Box>

          </Box>
        )}

      </Paper>
    </Box>
  );
};

export default AttendanceGraph;